import { addDays, differenceInCalendarDays } from "date-fns";

// Route-menu keys that sit next to a facility's presets. The one-night route
// is offered everywhere except whole-trip-only facilities, where a single
// night can't be booked on its own.
export const SINGLE_NIGHT_ROUTE = "single-night";
export const CUSTOM_ROUTE = "custom";

/**
 * The route behind the "One night" menu entry, and the default route for
 * facilities with no preset or that cover a whole area. It starts empty:
 * people watching one night pick one to three sites, almost never all of
 * them, so they choose the sites themselves.
 */
export function getSingleNightRoute() {
  return [{ index: 0, siteIds: [] }];
}

/**
 * The nights after each start date that a trip covers (night 2 onwards), so
 * the calendar can shade the rest of each trip next to its start day.
 */
export function getTripNightDates(startDates, nightCount) {
  if (!startDates?.length || !(nightCount > 1)) return [];
  const seen = new Set();
  const nights = [];
  for (const start of startDates) {
    for (let i = 1; i < nightCount; i++) {
      const night = addDays(start, i);
      const key = night.getTime();
      if (!seen.has(key)) {
        seen.add(key);
        nights.push(night);
      }
    }
  }
  return nights;
}

/**
 * True when the picked start dates look like the nights of one trip rather
 * than several possible starts: one unbroken run of days as long as the
 * route (or one longer, counting the walk-out day). Each picked day is a
 * separate possible start, so such a pick watches trips the person didn't
 * ask for.
 */
export function looksLikeOneTripsNights(startDates, nightCount) {
  if (!(nightCount > 1) || !startDates || startDates.length < 2) return false;
  if (
    startDates.length !== nightCount &&
    startDates.length !== nightCount + 1
  ) {
    return false;
  }
  const sorted = [...startDates].sort((a, b) => a - b);
  return sorted.every(
    (day, i) => i === 0 || differenceInCalendarDays(day, sorted[i - 1]) === 1,
  );
}

/**
 * One short label per night for summaries: the site's name, or the first
 * site's name plus how many alternatives. An empty night is null, so each
 * caller can say what fits where it shows.
 */
export function getNightLabels(nights, sites) {
  const names = new Map((sites || []).map((site) => [site.id, site.name]));
  return (nights || []).map(({ siteIds = [] }) => {
    if (siteIds.length === 0) return null;
    const first = names.get(siteIds[0]) || "1 site";
    return siteIds.length === 1 ? first : `${first} +${siteIds.length - 1}`;
  });
}
